import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import useEntityStore from '../../stores/entityStore';
import { entityService } from '../../services/entityService';
import { API_BASE_URL } from '../../api/config';
import { entityResolver } from '../../services/entityResolver';
import { getPlan, PLAN_IDS } from '../../config/planConfig';

export const apiConnectCommand: SelfContainedCommand = {
  id: 'api_connect',
  description: `🌐 API CONNECT: Make HTTP requests to external APIs
  
**USAGE:**
Make API calls to external services - authenticated, managed, and public APIs.

**THREE MODES:**

**1. AUTHENTICATED MODE (using APIConnection):**
- Use when API requires authentication (API keys, tokens, etc.)
- ⭐ **IMPORTANT WORKFLOW:**
  1. First check if integration exists: integration_list {}
  2. Find the connection (look for isAPIConnection: true)
  3. **READ THE API DOCS FIRST:** api_connection_read { connectionId: "..." }
     - This returns the serviceProfile with all endpoint documentation
     - Critical for getting correct methods, endpoints, and parameters
  4. Then make your API call: api_connect { connectionId, method, endpoint, ... }
- Example: integration_list → find CJDropshipping → api_connection_read → api_connect
- Credentials are automatically injected
- If you get endpoint errors, the error will include the serviceProfile

**2. MANAGED API MODE (River-managed, auto-enables):**
- Use for River-managed APIs like CJDropshipping
- Provide managedApiId directly (e.g., "managed_cjdropshipping")
- **Auto-enables on first use** - No explicit enable call needed!
- Backend creates user's instance automatically
- Example: api_connect({ managedApiId: "managed_cjdropshipping", method: "GET", endpoint: "/product/listV2" })
- Simpler than user-owned APIs (no credentials needed)

**⚠️ CRITICAL - WHEN CONNECTION MISSING:**
- If integration_list shows the service is not connected/available, call setup_integration tool
- Example: setup_integration with integrationType="api", serviceName="Stripe", purpose="..."
- DO NOT try to create APIConnection entities using entity_create
- DO NOT call api_connect if the connection doesn't exist (it will fail)
- The setup_integration tool will show a setup button for the user

**3. PUBLIC API MODE (no authentication):**
- Use when API is public and requires no authentication
- Provide baseUrl directly (e.g., "https://api.weather.gov")
- No APIConnection entity needed
- Perfect for open government data, public weather APIs, etc.

**AUTHENTICATED EXAMPLE (with discovery):**
\`\`\`json
// First, check what integrations are available:
// integration_list: {}
// Result: [
//   { "type": "CJDropshipping", "status": "active", "entityId": "69961...", "isAPIConnection": true },
//   { "type": "Gmail", "status": "connected", "entityId": "69845..." }
// ]

// Then use the entityId:
{
  "connectionId": "69961...",  // Use the entityId from integration_list
  "method": "POST",
  "endpoint": "/v1/customers",
  "body": {
    "email": "customer@example.com",
    "name": "John Doe"
  }
}
\`\`\`

**IF CONNECTION NOT FOUND:**
If integration_list doesn't show the service you need:
1. Call setup_integration tool with the service name (e.g., "Stripe", "Mixpanel", "CJDropshipping")
2. DO NOT call api_connect (it will fail)
3. DO NOT try to create the connection yourself
4. The setup_integration tool will show a button for the user to configure it

**PUBLIC API EXAMPLE:**
\`\`\`json
{
  "baseUrl": "https://api.weather.gov",
  "method": "GET",
  "endpoint": "/points/39.7456,-97.0892",
  "headers": {
    "User-Agent": "MyApp/1.0"
  }
}
\`\`\`

**SERVICE PROFILES:**
For authenticated connections, each APIConnection has a serviceProfile with API documentation.
⭐ READ THE SERVICE PROFILE CAREFULLY - it contains best practices and required workflows.`,

  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'connectionId',
        type: 'string',
        description: 'ID or name of the APIConnection entity to use. OPTIONAL - not needed for public APIs or when using managedApiId. Either connectionId, managedApiId, OR baseUrl must be provided.',
        required: false,
      },
      {
        name: 'managedApiId',
        type: 'string',
        description: 'Managed API ID (e.g., "managed_cjdropshipping") - auto-enables if not already enabled. Alternative to connectionId for River-managed APIs.',
        required: false,
      },
      {
        name: 'baseUrl',
        type: 'string',
        description: 'Base URL for the API (e.g., "https://api.weather.gov"). Required when connectionId is not provided for public APIs.',
        required: false,
      },
      {
        name: 'method',
        type: 'string',
        description: 'HTTP method: GET, POST, PUT, PATCH, DELETE',
        required: true,
      },
      {
        name: 'endpoint',
        type: 'string',
        description: 'API endpoint path (e.g., "/v1/customers" or "/points/39.7456,-97.0892")',
        required: true,
      },
      {
        name: 'body',
        type: 'object',
        description: 'Request body (for POST/PUT/PATCH)',
        required: false,
      },
      {
        name: 'queryParams',
        type: 'object',
        description: 'URL query parameters (for GET requests)',
        required: false,
      },
      {
        name: 'headers',
        type: 'object',
        description: 'Additional headers (merged with connection defaults for authenticated APIs, or used directly for public APIs)',
        required: false,
      },
    ],
  },
  
  examples: {
    good: [
      {
        input: {
          connectionId: 'stripe_main',
          method: 'POST',
          endpoint: '/v1/customers',
          body: {
            email: 'john@example.com',
            name: 'John Doe',
          },
        },
        expectedOutput: {
          status: 'success',
          message: 'API request successful',
          format: 'json',
          data: {
            id: 'cus_abc123',
            email: 'john@example.com',
            name: 'John Doe',
          },
        },
        description: 'Authenticated API: Create Stripe customer',
      },
      {
        input: {
          baseUrl: 'https://api.weather.gov',
          method: 'GET',
          endpoint: '/points/39.7456,-97.0892',
          headers: {
            'User-Agent': 'MyApp/1.0',
          },
        },
        expectedOutput: {
          status: 'success',
          message: 'API request successful',
          format: 'json',
          data: {
            properties: {
              forecast: 'https://api.weather.gov/gridpoints/TOP/31,80/forecast',
              forecastHourly: 'https://api.weather.gov/gridpoints/TOP/31,80/forecast/hourly',
            },
          },
        },
        description: 'Public API: Get weather point data from National Weather Service',
      },
      {
        input: {
          baseUrl: 'https://pokeapi.co/api/v2',
          method: 'GET',
          endpoint: '/pokemon/pikachu',
        },
        expectedOutput: {
          status: 'success',
          message: 'API request successful',
          format: 'json',
          data: {
            name: 'pikachu',
            height: 4,
            weight: 60,
          },
        },
        description: 'Public API: Get Pokemon data',
      },
    ],
    bad: [
      {
        input: {
          connectionId: 'invalid_connection',
          method: 'GET',
          endpoint: '/test',
        },
        error: 'APIConnection not found: invalid_connection',
        why: 'Connection does not exist',
        fix: 'Create the connection first or use the correct connection ID',
      },
      {
        input: {
          method: 'GET',
          endpoint: '/test',
        },
        error: 'Either connectionId or baseUrl must be provided',
        why: 'No connection specified',
        fix: 'Provide either connectionId (for authenticated APIs) or baseUrl (for public APIs)',
      },
    ],
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    console.log('[api_connect] 🚀 Handler called with params:', JSON.stringify(params, null, 2));
    
    const { connectionId, managedApiId, baseUrl, method, endpoint, body, queryParams, headers } = params;
    
    // Validate that either connectionId, managedApiId, or baseUrl is provided
    if (!connectionId && !managedApiId && !baseUrl) {
      return createErrorResult(
        'Either connectionId, managedApiId, or baseUrl must be provided',
        'For authenticated APIs, provide connectionId. For managed APIs, provide managedApiId. For public APIs, provide baseUrl.',
        'validation'
      );
    }
    
    try {
      let requestData: any;
      let connection: any = null;
      let serviceProfile: string | undefined = undefined;
      
      // MODE 1: Authenticated API using APIConnection entity
      if (connectionId) {
        console.log('[api_connect] 🔍 Resolving connection entity:', JSON.stringify({
          connectionId,
          hasContext: !!context,
          hasDocumentReferences: !!(context as any)?.documentReferences,
          documentReferencesCount: (context as any)?.documentReferences?.length || 0,
          documentReferencesList: (context as any)?.documentReferences?.map((d: any) => ({
            llmId: d.llmId,
            title: d.title
          }))
        }, null, 2));
        
        // ✅ FIX: Use entityResolver to properly resolve the connection
        // This handles documentReferences and falls back to API if needed
        try {
          const resolved = await entityResolver.resolveEntity(connectionId, context);
          
          console.log('[api_connect] ✅ Entity resolved:', JSON.stringify({
            entityId: resolved.entity?._id,
            entityLlmId: resolved.entity?.llmId,
            entityType: resolved.entity?.entityType,
            entityName: resolved.entity?.name,
            source: resolved.source
          }, null, 2));
          
          connection = resolved.entity;
          
          // Validate it's an APIConnection
          if (connection.entityType !== 'APIConnection') {
            console.error('[api_connect] ❌ Resolved entity is not an APIConnection:', JSON.stringify({
              requestedId: connectionId,
              actualType: connection.entityType,
              entityName: connection.name
            }, null, 2));
            
            return createErrorResult(
              `Not an APIConnection: ${connectionId}`,
              `Entity is of type ${connection.entityType}, expected APIConnection`,
              'validation'
            );
          }
        } catch (error: any) {
          // EntityResolver failed - try name-based lookup as fallback
          const entityStore = useEntityStore.getState();
          const apiConnections = entityStore.entities.filter(e => e.entityType === 'APIConnection');
          
          // Try to find by name (case-insensitive)
          connection = apiConnections.find(e => 
            e.name?.toLowerCase() === connectionId.toLowerCase()
          );
          
          if (!connection) {
            console.error('[api_connect] ❌ Connection resolution failed:', JSON.stringify({
              requestedId: connectionId,
              error: error.message,
              totalEntitiesInStore: entityStore.entities.length,
              apiConnectionsInStore: apiConnections.length,
              availableConnections: apiConnections.map(e => ({ 
                _id: e._id, 
                llmId: e.llmId,
                name: e.name
              })),
              documentReferences: (context as any)?.documentReferences?.map((d: any) => ({
                llmId: d.llmId,
                title: d.title
              }))
            }, null, 2));
            
            // Extract service name from connectionId for better error messaging
            let serviceName = connectionId;
            if (connectionId.includes('stripe')) serviceName = 'Stripe';
            else if (connectionId.includes('mixpanel')) serviceName = 'Mixpanel';
            else if (connectionId.includes('apollo')) serviceName = 'Apollo';
            else if (connectionId.includes('anthropic')) serviceName = 'Anthropic';
            else if (connectionId.includes('openweather')) serviceName = 'OpenWeather';
            
            return createErrorResult(
              `API connection not found: ${connectionId}`,
              `The ${serviceName} API connection needs to be set up. User can click the "Connect ${serviceName}" button below to configure their API keys.`,
              'validation',
              {
                data: {
                  connectionId,
                  serviceName,
                  needsSetup: true
                }
              }
            );
          }
          
          console.log('[api_connect] ✅ Found connection by name fallback:', JSON.stringify({
            connectionId,
            foundEntity: connection._id,
            llmId: connection.llmId,
            name: connection.name
          }, null, 2));
        }
        
        const skeleton = connection.skeleton;
        serviceProfile = skeleton.serviceProfile;
        
        console.log('[api_connect] ✅ Found connection:', JSON.stringify({
          _id: connection._id,
          name: connection.name,
          serviceName: connection.skeleton?.serviceName,
          status: connection.skeleton?.status,
          hasServiceProfile: !!serviceProfile,
          serviceProfileLength: serviceProfile?.length || 0,
          serviceProfilePreview: serviceProfile?.substring(0, 200) || 'N/A'
        }, null, 2));
        
        // Allow active or error status (error for auto-recovery attempts)
        if (skeleton.status !== 'active' && skeleton.status !== 'error') {
          return createErrorResult(
            `APIConnection is not active: ${connection.name}`,
            `Connection status is: ${skeleton.status}`,
            'validation'
          );
        }
        
        // For river_managed connections, serviceProfile was copied at install time (like Apps)
        if (skeleton.connectionType === 'river_managed' && !serviceProfile) {
          console.error('[api_connect] ❌ Missing serviceProfile on river_managed connection!');
          console.error('[api_connect] This should have been copied when user enabled the managed API');
          console.error('[api_connect] ManagedAPI ID:', skeleton.managedApiId);
        }
        
        // Stripe requires application/x-www-form-urlencoded — fix incorrect stored defaultHeaders
        let resolvedDefaultHeaders = skeleton.defaultHeaders ? { ...skeleton.defaultHeaders } : undefined;
        if (skeleton.baseUrl?.includes('api.stripe.com')) {
          resolvedDefaultHeaders = {
            ...resolvedDefaultHeaders,
            'Content-Type': 'application/x-www-form-urlencoded',
          };
        }

        requestData = {
          connectionId: connection._id,
          method: method.toUpperCase(),
          endpoint,
          body,
          queryParams,
          headers,
          baseUrl: skeleton.baseUrl,
          authType: skeleton.authType,
          defaultHeaders: resolvedDefaultHeaders,
          encryptedCredentials: skeleton.credentials,
        };
      } 
      // MODE 2: Managed API with auto-enable (NEW)
      else if (managedApiId) {
        console.log('[api_connect] ⚡ Managed API mode - auto-enable:', managedApiId);
        
        // Backend will auto-enable if needed
        requestData = {
          managedApiId,  // Pass to backend for auto-enable
          method: method.toUpperCase(),
          endpoint,
          body,
          queryParams,
          headers,
          // Backend will fill in baseUrl, authType, credentials from ManagedAPI template
        };
      }
      // MODE 3: Public API (no authentication required)
      else {
        console.log('[api_connect] 🌐 Public API mode - no authentication:', JSON.stringify({
          baseUrl,
          endpoint,
          method
        }, null, 2));
        
        requestData = {
          method: method.toUpperCase(),
          endpoint,
          body,
          queryParams,
          headers,
          baseUrl: baseUrl,
          authType: 'none',
          isPublicAPI: true,
        };
      }
      
      console.log('[api_connect] 📤 Sending request to backend:', JSON.stringify({
        endpoint: `${API_BASE_URL}/api-connections/execute`,
        requestData
      }, null, 2));
      
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api-connections/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestData),
      });
      
      console.log('[api_connect] 📥 Backend response received:', JSON.stringify({
        status: response.status,
        ok: response.ok,
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length')
      }, null, 2));
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('[api_connect] ❌ Backend error:', JSON.stringify(errorData, null, 2));

        if (response.status === 403 && errorData.detail?.error === 'PLAN_UPGRADE_REQUIRED') {
          const detail = errorData.detail;
          return {
            status: 'error',
            message: `This feature requires a ${getPlan(PLAN_IDS.BASIC).displayName} plan. Upgrade to continue.`,
            format: 'plaintext',
            data: {
              upgradeRequired: true,
              requiredPlan: detail.required_plan || 'basic',
              currentPlan: detail.current_plan || 'free',
            },
            error: {
              code: 'PLAN_UPGRADE_REQUIRED',
              category: 'billing',
              details: detail.message || 'Managed APIs require Plus plan',
              retryable: false,
            },
          } as any;
        }
        
        return createErrorResult(
          errorData.detail || `API request failed: ${response.status}`,
          `Status ${response.status}: ${errorData.detail || 'Unknown error'}`,
          'execution'
        );
      }
      
      console.log('[api_connect] 🔄 Parsing response as JSON...');
      
      let result;
      try {
        result = await response.json();
        console.log('[api_connect] ✅ JSON parsed successfully');
      } catch (parseError: any) {
        console.error('[api_connect] ❌ Failed to parse response:', parseError.message);
        return createErrorResult(
          'Failed to parse API response',
          parseError.message,
          'execution'
        );
      }
      
      console.log('[api_connect] 🔍 Analyzing response data:', JSON.stringify({
        success: result.success,
        statusCode: result.statusCode,
        status: result.status,
        dataType: typeof result.data,
        dataIsString: typeof result.data === 'string',
        dataLength: typeof result.data === 'string' ? result.data.length : null,
        dataIsObject: typeof result.data === 'object',
        dataIsArray: Array.isArray(result.data),
        responseTime: result.responseTime
      }, null, 2));
      
      if (typeof result.data === 'string' && result.data.length > 1000000) {
        console.warn('[api_connect] ⚠️ Large response detected:', {
          size: result.data.length,
          sizeKB: (result.data.length / 1024).toFixed(2),
          sizeMB: (result.data.length / 1024 / 1024).toFixed(2),
          firstChars: result.data.substring(0, 200),
          lastChars: result.data.substring(result.data.length - 200)
        });
      }
      
      if (!result.success && result.statusCode >= 400) {
        const retryHints: string[] = [];
        
        if (result.statusCode === 401) {
          retryHints.push('Authentication failed - verify credentials are correct');
        } else if (result.statusCode === 404) {
          retryHints.push('Endpoint not found - check API documentation for correct path');
          retryHints.push('Try listing available endpoints first (e.g., /api/2.0/events/names)');
        } else if (result.statusCode === 400) {
          retryHints.push('Bad request - check required parameters');
        }
        
        // If error is "Interface not found" or similar, include serviceProfile
        let errorMessage = `API returned ${result.statusCode}: ${result.status}`;
        const errorData = typeof result.data === 'object' ? result.data : {};
        
        // Check for errors indicating wrong endpoint/method
        const isEndpointError = 
          errorData.code === 1600101 || 
          errorData.code === 16900202 ||
          errorData.message?.includes('Interface not found') ||
          errorData.message?.includes('not supported');
        
        if (isEndpointError) {
          errorMessage += '\n\n⚠️ ENDPOINT/METHOD ERROR: The endpoint or HTTP method you used is incorrect.';
          retryHints.push('Wrong endpoint or method - READ THE SERVICE PROFILE BELOW CAREFULLY');
        }
        
        // ALWAYS include serviceProfile in error for authenticated connections
        if (connection && serviceProfile) {
          const profileStr = String(serviceProfile);
          console.log('[api_connect] 📖 Including serviceProfile in error response (length:', profileStr.length, ')');
          errorMessage += '\n\n📖 API SERVICE PROFILE (READ THIS TO GET CORRECT ENDPOINTS):\n\n' + profileStr;
        } else {
          console.warn('[api_connect] ⚠️ No serviceProfile available for error response');
        }
        
        return createErrorResult(
          errorMessage,
          `${result.status}. Response: ${typeof result.data === 'string' ? result.data.substring(0, 200) : JSON.stringify(result.data).substring(0, 200)}`,
          'execution',
          {
            error: {
              code: `HTTP_${result.statusCode}`,
              category: 'execution',
              details: result.data,
              suggestions: retryHints,
              retryable: true
            }
          }
        );
      }
      
      // Update usage stats for authenticated connections only
      if (connection) {
        const skeleton = connection.skeleton;
        await entityService.update(connection._id, {
          skeleton: {
            ...skeleton,
            lastUsedAt: new Date().toISOString(),
            usageCount: (skeleton.usageCount || 0) + 1,
            // Don't update status - leave it as-is
          },
        });
      }
      
      const successMessage = `${method.toUpperCase()} ${endpoint} - ${result.status || 'Success'}`;
      
      console.log('[api_connect] 🎉 Creating success result:', JSON.stringify({
        message: successMessage,
        dataType: typeof result.data,
        dataLength: typeof result.data === 'string' ? result.data.length : null,
        willTruncateData: typeof result.data === 'string' && result.data.length > 100000
      }, null, 2));
      
      // Truncate large string responses to prevent browser crashes
      let returnData = result.data;
      if (typeof result.data === 'string' && result.data.length > 100000) {
        const lineCount = result.data.split('\n').length;
        console.warn('[api_connect] 📦 Truncating large response:', {
          originalSize: result.data.length,
          originalLines: lineCount,
          truncatedSize: 100000,
          message: 'Response too large for browser - truncated to first 100KB'
        });
        returnData = result.data.substring(0, 100000) + `\n\n... [Response truncated - total ${lineCount} lines, ${(result.data.length / 1024).toFixed(0)}KB. Original response was ${(result.data.length / 1024 / 1024).toFixed(2)}MB]`;
      }
      
      console.log('[api_connect] 🎉 Final return data:', JSON.stringify({
        message: successMessage,
        dataType: typeof returnData,
        dataLength: typeof returnData === 'string' ? returnData.length : null
      }, null, 2));
      
      // Include serviceProfile in metadata for successful calls
      // This way AI can reference it in future calls
      const responseMetadata: any = {
        affectedEntities: connection ? [connection._id] : [],
      };
      
      // Add serviceProfile to metadata if available (helps AI learn correct patterns)
      if (connection && serviceProfile) {
        responseMetadata.serviceProfile = serviceProfile;
        responseMetadata.serviceName = connection.skeleton?.serviceName;
        responseMetadata.baseUrl = connection.skeleton?.baseUrl;
        console.log('[api_connect] 📖 Including serviceProfile in response metadata for future reference');
      }
      
      return createSuccessResult(
        successMessage,
        returnData,
        { metadata: responseMetadata }
      );
      
    } catch (error: any) {
      console.error('[api_connect] 💥 Exception caught:', JSON.stringify({
        message: error.message,
        stack: error.stack,
        error: error
      }, null, 2));
      
      // Don't update connection status on error - just log it
      console.error('[api_connect] Connection error logged but not persisted to entity');
      
      return createErrorResult(
        `API request failed: ${error.message}`,
        error,
        'execution'
      );
    }
  },
};
